import React, { useContext, useRef, useEffect } from 'react';
import { LogBox, View, Text, Pressable, Dimensions, Animated, Easing } from 'react-native';
import { createMaterialTopTabNavigator as createTabNavigator } from '@react-navigation/material-top-tabs';
import { useTheme } from 'react-native-paper';
import { AppContext } from '~context/appContext';

import { AuthStack } from '~navigators/stacks/authStack';
import { Blogs } from '~screens/blog/blogs';
import { Contact } from '~screens/contact/contact';
import { About } from '~screens/about/about';

import IconCase from '@@icons/case.svg';
import IconContact from '@@icons/contact.svg';
import IconAbout from '@@icons/about.svg';
import IconBlog from '@@icons/blog.svg';

const Tab = createTabNavigator();

export function CustomTabBar(props) {
	const theme = useTheme();
	const { width } = Dimensions.get('window');
	const tabWidth = width / props.state.routes.length;

	const translateX = useRef(new Animated.Value(props.state.index * tabWidth)).current;

	useEffect(() => {
		Animated.timing(translateX, {
			toValue: props.state.index * tabWidth,
			duration: 250,
			easing: Easing.out(Easing.cubic),
			useNativeDriver: true,
		}).start();
	}, [props.state.index]);
	return <View style={{ display: 'flex', flexDirection: 'row', height: 'auto', marginBottom: 2, backgroundColor: theme.colors.backdrop }}>
		{
			props.state.routes.map((route, index) => {
				const { options } = props.descriptors[route.key];
				const label = options.title.toUpperCase() || '';
				const isFocused = props.state.index === index;
				const onPress = () => {
					const event = props.navigation.emit({
						type: 'tabPress',
						target: route.key,
						canPreventDefault: true,
					});
					if (!isFocused && !event.defaultPrevented) {
						props.navigation.navigate(route.name, route.params);
					}
				};
				const icon = options.tabBarIcon({ focused: isFocused });
				return (
					<Pressable
						key={route.name}
						accessibilityState={isFocused ? { selected: true } : {}}
						onPress={onPress}
						style={{ flex: 1, diplay: 'flex', flexDirection: 'column', alignItems: 'center', paddingHorizontal: 3, paddingTop: 7, paddingBottom: 10, gap: 8, marginBottom: 8 }}
					>
						{icon}
						<Text style={{ textAlign: 'center', fontSize: 16, color: isFocused ? theme.colors.primary : theme.colors.text }}>
							{label}
						</Text>
					</Pressable>
				);
			})
		}
		<Animated.View
			style={{
				position: 'absolute',
				bottom: 0,
				left: 0,
				width: tabWidth,
				height: 3,
				backgroundColor: theme.colors.primary,
				transform: [{ translateX }],
			}}
		/>
	</View>;
}

export const BottomTabNavigator = ({navigation}) => {
	LogBox.ignoreLogs(['onAnimatedValueUpdate']); // Ignore log notification by message
	const theme = useTheme();
	const app = useContext(AppContext);
	
	return (
		<>
			<Tab.Navigator
				tabBarPosition="bottom"
				keyboardDismissMode="on-drag"
				tabBar={(props) => <CustomTabBar {...props} />}
				screenOptions={{
					tabBarActiveTintColor: theme.colors.primary,
					tabBarInactiveTintColor: theme.colors.text,
					tabBarLabelStyle: { fontSize: 14 },
					tabBarItemStyle: { padding: 3, paddingTop: 7, paddingBottom: 10 },
					tabBarShowLabel: true,
					tabBarShowIcon: true,
					tabBarIndicatorStyle: { backgroundColor: theme.colors.primary },
					tabBarPressColor: theme.dark
						? theme.colors.accent
						: theme.colors.primaryLight,
					tabBarPressOpacity: 0.9
				}}
			>
				<Tab.Screen
					id='CaseTab'
					name='CaseTab'
					component={AuthStack}
					options={{
						lazy: true,
						title: 'Case',
						tabBarShowIcon: true,
						tabBarIcon: ({ focused }) => {
							return (
								<IconCase
									fill={focused ? theme.colors.primary : theme.colors.accent}
									height={24} width={24}
								/>
							);
						}
					}}
					listeners={{
						tabPress: (e) => {
							// Skip to CaseStack if already logged in
							if (app.auth?.loggedIn){
								e.preventDefault();
								navigation.navigate('CasesDrawerNavigator');
							}
						},
						focus: () => {
							// Skip to CaseStack if already logged in
							if (app.auth?.loggedIn){
								navigation.navigate('CasesDrawerNavigator');
							}
						},
					}}
				/>
				<Tab.Screen
					name="BlogsTab"
					component={Blogs}
					options={{
						title: 'Blogs',
						tabBarShowIcon: true,
						tabBarIcon: ({ focused }) => {
							return (
								<IconBlog
									fill={focused ? theme.colors.primary : theme.colors.accent}
									height={24} width={24}
								/>
							);
						}
					}}
				/>
				<Tab.Screen
					name="ContactTab"
					component={Contact}
					options={{
						title: 'Contact',
						tabBarShowIcon: true,
						tabBarIcon: ({ focused }) => {
							return (
								<IconContact
									fill={focused ? theme.colors.primary : theme.colors.accent}
									height={24} width={24}
								/>
							);
						}
					}}
				/>
				<Tab.Screen
					name="AboutTab"
					component={About}
					options={{
						title: 'About',
						tabBarShowIcon: true,
						tabBarIcon: ({ focused }) => {
							return (
								<IconAbout
									fill={focused ? theme.colors.primary : theme.colors.accent}
									height={24} width={24}
								/>
							);
						}
					}}
				/>
			</Tab.Navigator>
		</>
	);
};